/* eslint-disable react/jsx-curly-newline */
import { truncateSearchText } from '@issuemanager/apps/client/utils';
import { AutocompleteInputChangeReason } from '@mui/material';
import { useMemo, useState } from 'react';
import { Control, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useInfiniteGrcObjects } from '../../../features/grc-objects/hooks';
import { useFeatureFlags } from '../../../features/users/hooks/use-feature-flags';
import { ExtendedControlledAutocomplete } from '../extended-controlled-autocomplete';

export interface GrcObjectSelectorProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  'data-testid'?: string;
  readOnly?: boolean;
  rules?: any;
  onObjectSelect?: (object: { id: string; label: string } | null) => void;
}

export function GrcObjectSelector<T extends FieldValues>(
  props: GrcObjectSelectorProps<T>,
) {
  const {
    name,
    control,
    label,
    'data-testid': dataTestId,
    readOnly,
    rules,
    onObjectSelect,
  } = props;

  const { t } = useTranslation(['common']);
  const [filterByText, setFilterByText] = useState('');
  const isRelationshipsEnabled = useFeatureFlags(
    'issueRelationships',
  ) as boolean;

  const grcObjectsQuery = useInfiniteGrcObjects({
    searchTerm: filterByText,
    enabled: isRelationshipsEnabled,
    handleError: true,
  });

  const options = useMemo(
    () =>
      grcObjectsQuery.data?.pages.flatMap((page) =>
        page.data.map((obj) => ({
          id: obj.id,
          label: obj.name,
        })),
      ) ?? [],
    [grcObjectsQuery.data],
  );

  const handleTextChange = (
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (reason === 'input') {
      setFilterByText(truncateSearchText(value));
    }
  };

  const handleChange = (
    option: { id: string; label: string } | { id: string; label: string }[],
  ) => {
    if (onObjectSelect) {
      const singleOption = Array.isArray(option) ? option[0] : option;
      onObjectSelect(singleOption || null);
    }
  };

  return (
    <ExtendedControlledAutocomplete
      name={name}
      control={control}
      rules={rules}
      label={label ?? ''}
      data-testid={dataTestId}
      loading={grcObjectsQuery.isLoading || grcObjectsQuery.isFetchingNextPage}
      options={options}
      onTextChange={handleTextChange}
      onChangeProp={onObjectSelect ? handleChange : undefined}
      readOnly={readOnly}
      placeholder={t('inputs.listBox.placeholder')}
    />
  );
}
